import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRefreshUser, useUser } from "@/features/auth/store";
import { authService } from "@/features/auth/services/authService";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";

type ProfileTab = "info" | "name" | "password";

const TABS: Array<{ key: ProfileTab; label: string }> = [
  { key: "info", label: "معلومات الحساب" },
  { key: "name", label: "تعديل الاسم" },
  { key: "password", label: "كلمة المرور" },
];

const DEFAULT_TAB: ProfileTab = "info";

function parseTab(raw: string | null): ProfileTab {
  if (raw === "name") return "name";
  if (raw === "password") return "password";
  return DEFAULT_TAB;
}

const profileSchema = z.object({
  firstName: z.string().trim().optional(),
  lastName: z.string().trim().optional(),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "كلمة المرور الحالية مطلوبة"),
    newPassword: z.string().min(8, "كلمة المرور الجديدة يجب أن تكون 8 أحرف على الأقل"),
    confirmPassword: z.string().min(1, "تأكيد كلمة المرور مطلوب"),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "كلمتا المرور غير متطابقتين",
    path: ["confirmPassword"],
  });

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  const user = useUser();
  const refreshUser = useRefreshUser();
  const { data: departmentsData } = useDepartments({ limit: 200 });
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"));
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const setTab = (next: ProfileTab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === DEFAULT_TAB) params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  };

  const departmentName = departmentsData?.items?.find(
    (d) => d.id === user?.departmentId,
  )?.nameAr;

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
    values: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  async function onSaveProfile(data: ProfileForm) {
    setSavingProfile(true);
    try {
      await authService.updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
      });
      await refreshUser();
      toastMutationSuccess(null, { fallbackMessage: "تم تحديث الملف الشخصي" });
    } catch (e) {
      handleApiError(e);
    } finally {
      setSavingProfile(false);
    }
  }

  async function onChangePassword(data: PasswordForm) {
    setSavingPassword(true);
    try {
      await authService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      passwordForm.reset();
      toastMutationSuccess(null, {
        fallbackMessage: "تم تغيير كلمة المرور — يرجى تسجيل الدخول مجدداً",
      });
    } catch (e) {
      handleApiError(e);
    } finally {
      setSavingPassword(false);
    }
  }

  return (
    <AdminPageShell title="الملف الشخصي">
      <div className="mx-auto max-w-2xl">
        <TabsList className="mb-4 w-full sm:w-auto">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              className="flex-1 sm:flex-none"
              data-state={tab === t.key ? "active" : "inactive"}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <Card>
          <CardContent className="pt-6">
            {tab === "info" && (
              <div className="grid gap-4 text-right sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <Label>البريد الإلكتروني</Label>
                  <Input value={user?.email ?? ""} disabled dir="ltr" />
                </div>
                <div className="space-y-1">
                  <Label>الدور</Label>
                  <Input
                    value={user?.role ? getRoleDisplayName(user.role) : ""}
                    disabled
                  />
                </div>
                <div className="space-y-1">
                  <Label>الدائرة</Label>
                  <Input
                    value={departmentName ?? user?.departmentId ?? "—"}
                    disabled
                  />
                </div>
              </div>
            )}

            {tab === "name" && (
              <form
                onSubmit={profileForm.handleSubmit(onSaveProfile)}
                className="space-y-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1 text-right">
                    <Label htmlFor="firstName">الاسم الأول</Label>
                    <Input
                      id="firstName"
                      {...profileForm.register("firstName")}
                    />
                  </div>
                  <div className="space-y-1 text-right">
                    <Label htmlFor="lastName">اسم العائلة</Label>
                    <Input id="lastName" {...profileForm.register("lastName")} />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={savingProfile}>
                    حفظ التغييرات
                    {savingProfile && <Loader className="mr-2" />}
                  </Button>
                </div>
              </form>
            )}

            {tab === "password" && (
              <form
                onSubmit={passwordForm.handleSubmit(onChangePassword)}
                className="space-y-4"
              >
                <div className="space-y-1 text-right">
                  <Label htmlFor="currentPassword">كلمة المرور الحالية</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    {...passwordForm.register("currentPassword")}
                  />
                  {passwordForm.formState.errors.currentPassword && (
                    <p className="text-sm text-destructive">
                      {passwordForm.formState.errors.currentPassword.message}
                    </p>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1 text-right">
                    <Label htmlFor="newPassword">كلمة المرور الجديدة</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      {...passwordForm.register("newPassword")}
                    />
                    {passwordForm.formState.errors.newPassword && (
                      <p className="text-sm text-destructive">
                        {passwordForm.formState.errors.newPassword.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 text-right">
                    <Label htmlFor="confirmPassword">تأكيد كلمة المرور</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      {...passwordForm.register("confirmPassword")}
                    />
                    {passwordForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-destructive">
                        {passwordForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={savingPassword}>
                    تغيير كلمة المرور
                    {savingPassword && <Loader className="mr-2" />}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageShell>
  );
}
